import React, { useState } from 'react';
import { useForm } from "react-hook-form";
import Spinner from '../../components/Spinner';
import { AuthInput, PasswordInput } from '../../components/ui';
import { API, Auth } from '../../utils/amplifyWrapper';
import { toast } from 'react-toastify';
import { createBrowserHistory } from 'history';
import { Modal } from "react-bootstrap";
import * as Sentry from '@sentry/react';
import { fetchIpInfoService } from '../../utils/Country'
import AuthSpritesImg from "../../assets/auth-sprites.svg"
import { goToCurrentIntegration } from '../../utils/General';
import { useAuthStore } from '../../zustand-store/useAuthStore';
import GoogleOAuthButton from './GoogleOAuthButton';
import { trackLoginOncePerDay, markLoginTrackedForToday } from '../../services/mixpanel';
const classNames = require('classnames');

export default function SignIn() {

    const [isLoading, setIsLoading] = useState(false);
    const history = createBrowserHistory();
    const [formState, setFormState] = React.useState("signin");
    const [username, setUsername] = useState(null);
    const [password, setPassword] = useState(null);
    const loginForm = useForm();
    const { formState: { errors: loginErrors } } = loginForm;
    const confirmSignUpForm = useForm();
    const { formState: { errors: confirmSignUpErrors } } = confirmSignUpForm;
    const [user, setUser] = useState(null);
    const newPasswordForm = useForm();
    const { formState: { errors: newPasswordErrors } } = newPasswordForm;
    const [showNewPasswordModal, setShowNewPasswordModal] = useState(false);
    const [isSettingNewPassword, setIsSettingNewPassword] = useState(false);
    const [showPass, setShowPass] = useState(false);
    const { setUser: setGlobalUser, setAuthState: setGlobalAuthState } = useAuthStore();

    React.useEffect(() => {
        setGlobalUser(null);
    }, [setGlobalUser]);

    const onSubmitLoginForm = async data => {
        const { username, password } = data;
        setUsername(username);
        setPassword(password);
        setIsLoading(true);

        Auth.signIn(username, password).then(async (response) => {
            console.log({ response: response.attributes })
            setUser(response);

            if (response.challengeName === 'NEW_PASSWORD_REQUIRED') {
                setShowNewPasswordModal(true);
                setIsLoading(false);
                return;
            }

            setGlobalUser(response);
            // GROW-81: Login event for a returning email/password user (deduped to once/day,
            // shared with the Google and session-resume paths).
            trackLoginOncePerDay({ method: 'email' });
            await goToCurrentIntegration(history)
            setGlobalAuthState("signedin");
            setIsLoading(false);

        }).catch(async (error) => {
            if (error.name === 'UserNotConfirmedException') {
                return Auth.resendSignUp(username)
                    .then(() => {
                        setIsLoading(false);
                        setFormState('signUpConfirmationPending')
                    }).catch(error => {
                        toast.error(error.message);
                        setIsLoading(false);
                    });
            }

            if (error.name === 'UserNotFoundException') {
                try {
                    const accountInfo = await API.post('main', '/public/account-provider', {
                        body: { email: username }
                    });
                    if (accountInfo?.data?.provider === 'google') {
                        toast.error('This account was created using Google. Please sign in with Google.');
                        setIsLoading(false);
                        return;
                    }
                } catch (lookupErr) {
                    console.error('account-provider lookup failed:', lookupErr);
                }
            }

            toast.error(error.message);
            setIsLoading(false);
        });
    };

    const onSubmitConfirmSignUp = async data => {
        const { code } = data;
        setIsLoading(true);

        const userLocationData = await fetchIpInfoService()
        const location = { country: userLocationData.country }

        Auth.confirmSignUp(username, code.trim()).then((response) => {
            Auth.signIn(username, password).then((response) => {
                setGlobalUser(response);
                // GROW-81: new account, not a return — consume today's login slot so the
                // session-resume path doesn't count signup day as a Login.
                markLoginTrackedForToday();
                Auth.currentAuthenticatedUser().then(user => {
                    const organisation = user.attributes["custom:org"];
                    API.post(`main`, `/organisation`, { body: { name: organisation, location }, headers: { "Content-Type": "application/json" } })
                        .then((response) => {
                            setIsLoading(false);
                            if (response.success) {
                                history.push(`/onboarding/work?orgId=${response.data.orgId}&integrationId=${response.data.id}`);
                            } else {
                                setGlobalAuthState("signedin");
                            }
                        });
                });
            })
        }).catch((error) => {
            toast.error(error.message);
            setIsLoading(false);
        });
    }

    const onResendCode = async () => {
        Auth.resendSignUp(username)
            .then(() => { toast.success("Code has been resent successfully."); })
            .catch(error => { toast.error(error.message); })
            .finally(() => { setIsLoading(false); });
    }

    const onSubmitNewPasswordForm = async data => {
        const { given_name, family_name, password } = data;
        setIsSettingNewPassword(true);
        Auth.completeNewPassword(user, password, { given_name, family_name })
            .then(user => {
                setGlobalUser(user);
                history.push(`/`);
                setGlobalAuthState("signedin");
            })
            .catch(e => { Sentry.captureException(e); toast.error("Something went wrong."); })
            .finally(() => { setIsSettingNewPassword(false); });
    };

    return (
        <div className="row my-3 my-md-0">
            <div className="col-md-8 offset-md-2 col-lg-6 offset-lg-3 px-0">

                {formState === "signin" && (
                    <div style={{ width: '100%' }}>
                        <h2 style={{ fontSize: 26, fontWeight: 800, color: '#0F172A', marginBottom: 7, textAlign: 'center', letterSpacing: '-.02em' }}>Welcome back</h2>
                        <p style={{ fontSize: 14, color: '#64748B', textAlign: 'center', marginBottom: 32 }}>
                            Don't have an account?{' '}
                            <span
                                style={{ color: '#2563EB', fontWeight: 600, cursor: 'pointer' }}
                                onClick={() => setGlobalAuthState("signup")}
                            >
                                Sign up free
                            </span>
                        </p>

                        <form onSubmit={loginForm.handleSubmit(onSubmitLoginForm)}>
                            <div style={{ marginBottom: 14 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Email address</label>
                                <input
                                    type="email"
                                    placeholder="you@company.com"
                                    style={{ width: '100%', padding: '10px 13px', border: `1px solid ${loginErrors.username ? '#FECACA' : '#E2E8F0'}`, borderRadius: 8, fontSize: 14, color: '#0F172A', outline: 'none', background: '#F8FAFC', boxSizing: 'border-box', transition: 'border .15s' }}
                                    onFocus={e => e.target.style.borderColor = '#2563EB'}
                                    onBlur={e => e.target.style.borderColor = loginErrors.username ? '#FECACA' : '#E2E8F0'}
                                    {...loginForm.register("username", { required: true, pattern: /^[\w-.+]+@([\w-]+\.)+[\w-]{2,6}$/g })}
                                />
                                {loginErrors.username && (
                                    <p style={{ fontSize: 12, color: '#DC2626', marginTop: 4, marginBottom: 0 }}>Please enter a valid email address.</p>
                                )}
                            </div>

                            <div style={{ marginBottom: 8 }}>
                                <label style={{ display: 'block', fontSize: 13, fontWeight: 600, color: '#334155', marginBottom: 6 }}>Password</label>
                                <div style={{ position: 'relative' }}>
                                    <input
                                        type={showPass ? 'text' : 'password'}
                                        placeholder="••••••••"
                                        style={{ width: '100%', padding: '10px 40px 10px 13px', border: `1px solid ${loginErrors.password ? '#FECACA' : '#E2E8F0'}`, borderRadius: 8, fontSize: 14, color: '#0F172A', outline: 'none', background: '#F8FAFC', boxSizing: 'border-box', transition: 'border .15s' }}
                                        onFocus={e => e.target.style.borderColor = '#2563EB'}
                                        onBlur={e => e.target.style.borderColor = loginErrors.password ? '#FECACA' : '#E2E8F0'}
                                        {...loginForm.register("password", { required: true })}
                                    />
                                    <button
                                        type="button"
                                        onClick={() => setShowPass(p => !p)}
                                        style={{ position: 'absolute', right: 11, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', color: '#94A3B8', padding: 0, display: 'flex', alignItems: 'center' }}
                                    >
                                        <svg width="17" height="17" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round">
                                            {showPass
                                                ? <><path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" /><line x1="1" y1="1" x2="23" y2="23" /></>
                                                : <><path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" /><circle cx="12" cy="12" r="3" /></>
                                            }
                                        </svg>
                                    </button>
                                </div>
                                {loginErrors.password && (
                                    <p style={{ fontSize: 12, color: '#DC2626', marginTop: 4, marginBottom: 0 }}>Password is required.</p>
                                )}
                            </div>

                            <div style={{ textAlign: 'right', marginBottom: 24 }}>
                                <span
                                    style={{ fontSize: 13, color: '#2563EB', fontWeight: 500, cursor: 'pointer' }}
                                    onClick={() => setGlobalAuthState("forgotPassword")}
                                >
                                    Forgot password?
                                </span>
                            </div>

                            <button
                                type={isLoading ? "button" : "submit"}
                                style={{ width: '100%', padding: '11px', background: isLoading ? '#93C5FD' : '#2563EB', color: 'white', border: 'none', borderRadius: 8, fontSize: 15, fontWeight: 700, cursor: isLoading ? 'default' : 'pointer', marginBottom: 14, transition: 'background .15s', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8 }}
                            >
                                {isLoading ? <Spinner color="white" /> : 'Continue'}
                            </button>
                        </form>

                        <GoogleOAuthButton buttonText="Continue with Google" />

                        <p style={{ fontSize: 11.5, color: '#94A3B8', textAlign: 'center', marginTop: 24, lineHeight: 1.6 }}>
                            By continuing, you agree to our{' '}
                            <a href={process.env.REACT_APP_TERMS_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB', textDecoration: 'none' }}>Terms of Service</a>
                            {' '}and{' '}
                            <a href={process.env.REACT_APP_PRIVACY_POLICY_URL} target="_blank" rel="noopener noreferrer" style={{ color: '#2563EB', textDecoration: 'none' }}>Privacy Policy</a>
                        </p>
                    </div>
                )}

                {formState === "signUpConfirmationPending" && (
                    <form className="mt-5" onSubmit={confirmSignUpForm.handleSubmit(onSubmitConfirmSignUp)}>
                        <h4 className="fw-bold text-body-emphasis text-center mb-1">Verify your email</h4>
                        <p className="text-center mb-4" style={{ color: 'var(--bs-secondary-color)', fontSize: 14 }}>
                            We've sent a verification code to your email.
                        </p>
                        <div className="mb-3">
                            <label className="form-label fw-semibold">Verification code</label>
                            <input
                                type="text"
                                name="code"
                                placeholder="Enter code"
                                className={classNames("form-control", { "is-invalid": confirmSignUpErrors.code })}
                                {...confirmSignUpForm.register("code", { required: true })}
                            />
                            {confirmSignUpErrors.code && (
                                <div className="invalid-feedback">Code is required.</div>
                            )}
                        </div>

                        <button
                            type={isLoading ? "button" : "submit"}
                            className="btn btn-primary w-100"
                            style={{ height: 44 }}
                        >
                            {isLoading ? <Spinner color="white" /> : 'Verify & Continue'}
                        </button>

                        <p className="mt-3 text-center" style={{ fontSize: 13, color: 'var(--bs-secondary-color)' }}>
                            Didn't receive a code? Check your spam or{' '}
                            <span className="text-primary" style={{ cursor: 'pointer' }} onClick={onResendCode}>
                                resend
                            </span>.
                        </p>
                    </form>
                )}

                <Modal show={showNewPasswordModal} onHide={() => setShowNewPasswordModal(false)}>
                    <Modal.Body>
                        <form className="p-3" id="new-password-form" onSubmit={newPasswordForm.handleSubmit(onSubmitNewPasswordForm)}>
                            <h5 className="fw-bold text-body-emphasis mb-4">Complete your account setup</h5>
                            <AuthInput
                                label="First Name"
                                name="given_name"
                                type="text"
                                register={newPasswordForm.register}
                                errors={newPasswordErrors.given_name}
                                validation={{ required: true }}
                                backgroundImage={AuthSpritesImg}
                                backgroundPosition="4px 0px"
                            />
                            <AuthInput
                                label="Last Name"
                                name="family_name"
                                type="text"
                                register={newPasswordForm.register}
                                errors={newPasswordErrors.family_name}
                                validation={{ required: true }}
                                backgroundImage={AuthSpritesImg}
                                backgroundPosition="4px 0px"
                            />
                            <PasswordInput
                                label="New Password"
                                name="password"
                                register={newPasswordForm.register}
                                errors={newPasswordErrors.password}
                                validation={{ required: true }}
                                backgroundImage={AuthSpritesImg}
                                backgroundPosition="4px -70px"
                            />
                        </form>
                    </Modal.Body>
                    <Modal.Footer>
                        <button
                            type={isSettingNewPassword ? "button" : "submit"}
                            form="new-password-form"
                            className="btn btn-primary w-100"
                            style={{ height: 44 }}
                        >
                            {isSettingNewPassword ? <Spinner color="white" /> : 'Set Password & Continue'}
                        </button>
                    </Modal.Footer>
                </Modal>
            </div>
        </div>
    );
}
